/**
 * Geography > Zones - full CRUD over GeoZone (WPS-222). Replaces the old
 * Manage > Products > Geo Zones page (create-only); zone types and parent
 * options come from the API, never a frontend constant.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, Plus, Upload } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import type { ProductFilterFacet } from '@/shared/components/ui/product-filter-menu'
import type { FormFieldMeta } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { BulkImportDialog } from '@/admin/components/imports/bulk-import-dialog'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  useCreateGeoZone,
  useDeleteGeoZone,
  useGeoZones,
  useGeoZonesAll,
  useUpdateGeoZone,
  type GeoZone,
} from '@/admin/hooks/use-geo'
import { useGeoMeta, useRefreshGeoData } from '@/admin/hooks/use-geo-reference'

import { CodeCell, GeographySectionShell, TextCell } from './geography-shared'
import { useCountLine, useZoneTypeLabel, zoneLabel } from './geography-utils'
import { NONE, nullable, saveWithFieldErrors, useListState } from './section-crud'

export function ZonesSection() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canCreate = can('geo-zones.create')
  const canEdit = can('geo-zones.edit')
  const canDelete = can('geo-zones.delete')

  const list = useListState()

  const refreshGeoData = useRefreshGeoData()
  const [editing, setEditing] = useState<GeoZone | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<GeoZone | null>(null)
  const [showImport, setShowImport] = useState(false)

  const { data: meta } = useGeoMeta()
  const { data, isLoading, isFetching, isError, refetch } = useGeoZones(list.params)
  const { data: allZones } = useGeoZonesAll()
  const createZone = useCreateGeoZone()
  const updateZone = useUpdateGeoZone()
  const deleteZone = useDeleteGeoZone()
  const typeLabel = useZoneTypeLabel()

  const rows = data?.results ?? []
  const countLine = useCountLine(
    data?.count,
    t('geography.zones.countOne', 'zone'),
    t('geography.zones.countMany', 'zones'),
  )

  const facets = useMemo<ProductFilterFacet[]>(
    () => [
      {
        key: 'zone_type',
        label: t('geography.zones.field.zoneType', 'Zone type'),
        kind: 'field',
        options: meta?.zone_types ?? [],
      },
    ],
    [meta?.zone_types, t],
  )

  const parentOptions = useMemo(
    () => [
      { value: NONE, label: t('geography.common.none', 'None') },
      ...(allZones?.results ?? [])
        .filter((z) => z.uuid !== editing?.uuid)
        .map((z) => ({ value: z.uuid, label: zoneLabel(z.name, z.code) })),
    ],
    [allZones?.results, editing?.uuid, t],
  )

  const fields = useMemo<FormFieldMeta[]>(
    () => [
      {
        code_name: 'name',
        display_name: t('geography.zones.field.name', 'Zone name'),
        field_type: 'TEXT',
        required: true,
      },
      {
        code_name: 'code',
        display_name: t('geography.zones.field.code', 'Zone code'),
        field_type: 'TEXT',
        required: false,
        description: t(
          'geography.zones.field.codeHint',
          'Stable identifier such as a plant or zone number. Unique across zones.',
        ),
      },
      {
        code_name: 'zone_type',
        display_name: t('geography.zones.field.zoneType', 'Zone type'),
        field_type: 'SELECT',
        required: true,
        options: meta?.zone_types ?? [],
      },
      {
        code_name: 'parent_zone_uuid',
        display_name: t('geography.zones.field.parent', 'Parent zone'),
        field_type: 'SELECT',
        required: false,
        options: parentOptions,
        configuration_data: { searchable: true },
      },
    ],
    [meta?.zone_types, parentOptions, t],
  )

  const values = useMemo<Record<string, unknown>>(
    () =>
      editing
        ? {
            name: editing.name,
            code: editing.code,
            zone_type: editing.zone_type,
            parent_zone_uuid: editing.parent_zone_uuid ?? NONE,
          }
        : { zone_type: 'geo_zone', parent_zone_uuid: NONE },
    [editing],
  )

  const columns = useMemo<DataTableColumn<GeoZone>[]>(
    () => [
      {
        id: 'name',
        header: t('geography.zones.column.name', 'Zone'),
        sortable: true,
        sortField: 'name',
        cell: (r) => <TextCell value={r.name} />,
      },
      { id: 'code', header: t('geography.common.code', 'Code'), cell: (r) => <CodeCell value={r.code} /> },
      {
        id: 'zone_type',
        header: t('geography.zones.column.type', 'Type'),
        cell: (r) => <TextCell muted value={typeLabel(r.zone_type)} />,
      },
      {
        id: 'parent',
        header: t('geography.zones.column.parent', 'Parent'),
        cell: (r) => <TextCell muted value={r.parent_zone_name || '-'} />,
      },
      {
        id: 'postal_code_count',
        header: t('geography.zones.column.postalCodes', 'Postal codes'),
        cell: (r) => <TextCell muted value={r.postal_code_count.toLocaleString()} />,
      },
      {
        id: 'psa_count',
        header: t('geography.zones.column.psaMappings', 'PSA mappings'),
        cell: (r) => <TextCell muted value={r.psa_count.toLocaleString()} />,
      },
      {
        id: 'child_count',
        header: t('geography.zones.column.children', 'Child zones'),
        cell: (r) => <TextCell muted value={r.child_count.toLocaleString()} />,
      },
      {
        id: 'actions',
        header: '',
        cell: (r) =>
          canEdit || canDelete ? (
            <div className="flex justify-end gap-2">
              {canEdit && (
                <Button
                  variant="ghost"
                  size="xs"
                  data-testid={`geo-zones-edit-${r.uuid}`}
                  onClick={() => {
                    setEditing(r)
                    setFormOpen(true)
                  }}
                >
                  {t('geography.common.edit', 'Edit')}
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="xs"
                  data-testid={`geo-zones-delete-${r.uuid}`}
                  onClick={() => setDeleting(r)}
                >
                  {t('geography.common.delete', 'Delete')}
                </Button>
              )}
            </div>
          ) : null,
      },
    ],
    [canDelete, canEdit, t, typeLabel],
  )

  return (
    <div data-tour="geo-zones-list">
      <GeographySectionShell
        testId="geography-zones"
        title={t('geography.zones.title', 'Zones')}
        description={t(
          'geography.zones.description',
          'Every geographic zone the platform resolves addresses into - plant service areas, zones, regions, states. Postal codes, PSA mappings and claim routing all reference these.',
        )}
        countLine={countLine}
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={t('geography.zones.searchPlaceholder', 'Search by name or code')}
        facets={facets}
        filterValues={list.filterValues}
        onFilterValuesChange={list.setFilterValues}
        actions={
          <div data-tour="zone-types" className="flex items-center gap-2">
            {canCreate && (
              <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
                <Upload className="h-3.5 w-3.5" />
                {t('geography.common.import', 'Import')}
              </Button>
            )}
            {canCreate && (
              <Button
                size="sm"
                data-testid="geo-zones-add-btn"
                onClick={() => {
                  setEditing(null)
                  setFormOpen(true)
                }}
              >
                <Plus className="h-3.5 w-3.5" />
                {t('geography.zones.add', 'Add zone')}
              </Button>
            )}
          </div>
        }
      >
        <DataTable<GeoZone>
          tableId="geography-zones"
          columns={columns}
          data={rows}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={() => refetch()}
          page={list.page}
          pageSize={list.pageSize}
          totalCount={data?.count ?? 0}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
          rowKey={(r) => r.uuid}
          emptyStateNode={
            <EmptyState
              icon={MapPin}
              title={t('geography.zones.emptyTitle', 'No zones yet')}
              description={t(
                'geography.zones.emptyDescription',
                'Add a zone or import a spreadsheet to start resolving addresses.',
              )}
            />
          }
        />
      </GeographySectionShell>

      <EditFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open)
          if (!open) setEditing(null)
        }}
        title={
          editing
            ? t('geography.zones.editTitle', 'Edit zone')
            : t('geography.zones.addTitle', 'Add zone')
        }
        fields={fields}
        values={values}
        isPending={createZone.isPending || updateZone.isPending}
        onSave={(v, _reason, helpers) =>
          saveWithFieldErrors(
            async () => {
              const payload = {
                name: String(v.name ?? '').trim(),
                code: String(v.code ?? '').trim(),
                zone_type: String(v.zone_type),
                parent_zone_uuid: nullable(v.parent_zone_uuid),
              }
              if (editing) {
                await updateZone.mutateAsync({ uuid: editing.uuid, data: payload })
              } else {
                await createZone.mutateAsync(payload)
              }
            },
            helpers,
            editing
              ? t('geography.zones.updated', 'Zone updated.')
              : t('geography.zones.created', 'Zone created.'),
            t('geography.zones.saveFailed', 'Could not save the zone.'),
          )
        }
      />

      <ConfirmDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
        title={t('geography.zones.deleteTitle', 'Delete zone?')}
        description={
          deleting
            ? t(
                'geography.zones.deleteDescription',
                'Postal codes and mappings that reference {{name}} will no longer resolve to it.',
                { name: zoneLabel(deleting.name, deleting.code) },
              )
            : undefined
        }
        isPending={deleteZone.isPending}
        onConfirm={async () => {
          if (!deleting) return
          try {
            await deleteZone.mutateAsync(deleting.uuid)
            toast.success(t('geography.zones.deleted', 'Zone deleted.'))
            setDeleting(null)
          } catch (err) {
            toastApiError(err, t('geography.zones.deleteFailed', 'Could not delete the zone.'))
          }
        }}
      />

      <BulkImportDialog
        onComplete={refreshGeoData}
        open={showImport}
        onOpenChange={setShowImport}
        target="geo_zones"
        title={t('geography.zones.importTitle', 'Import zones')}
      />
    </div>
  )
}
